import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import dompurify from "isomorphic-dompurify";
import { translateBackendErrorCode } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { fetchUserPoints, useDispatch } from "@jti/store";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { DEPRECATED_Typography as Typography, Dialog, Info, MessageBox, MessageBoxVariant, replaceStringPlaceholder, useTranslations, } from "@jti/ui";
import CheckMark from "@jti/ui/icons/checkmark-round.svg";
import { DialogCard } from "./DialogCard";
import { RedeemDialogButtons } from "./RedeemDialogButtons";
import styles from "./RedeemDialog.module.scss";
const { sanitize } = dompurify;
export var DIALOG_STEP;
(function (DIALOG_STEP) {
    DIALOG_STEP[DIALOG_STEP["SUMMARY"] = 0] = "SUMMARY";
    DIALOG_STEP[DIALOG_STEP["CONFIRMATION"] = 1] = "CONFIRMATION";
})(DIALOG_STEP || (DIALOG_STEP = {}));
export const RedeemDialog = ({ prizeData, pointsBalance, onCloseDialog, labels }) => {
    const [dialogStep, setDialogStep] = useState(DIALOG_STEP.SUMMARY);
    const [quantity, setQuantity] = useState(1);
    const [{ codes, type }, setRewardCodes] = useState({ codes: [], type: "" });
    const [isQueryInProgress, setIsQueryInProgress] = useState(false);
    const dispatch = useDispatch();
    const { __ } = useTranslations();
    const { confirmationMessage, confirmationMessagePlural, primaryCta, secondaryCta, costLabel, availablePointsLabel, balanceAfterRedemptionLabel, emailLabel, emailHint, codeLabel, codeInfo, externalCodeButtonLabel, unitLabel, pointsLabel, } = labels;
    const confirmationLabel = useMemo(() => {
        const message = quantity > 1 ? confirmationMessagePlural : confirmationMessage;
        if (!prizeData.points || !prizeData.name)
            return message;
        const placeholders = [
            {
                placeholder: "qty",
                value: quantity,
            },
            {
                placeholder: "redeemedPoints",
                value: prizeData.points * quantity,
            },
            {
                placeholder: "voucherName",
                value: __(prizeData.name),
            },
        ];
        return replaceStringPlaceholder(message, placeholders);
    }, [quantity, confirmationMessage, confirmationMessagePlural, prizeData.points, prizeData.name]);
    if (!prizeData?.prize_uuid)
        return null;
    const handleCloseDialog = () => {
        onCloseDialog();
        setQuantity(1);
        setDialogStep(DIALOG_STEP.SUMMARY);
    };
    const buyPrize = async () => {
        const user = getUser();
        if (user?.ciam_token && user.uuid) {
            setIsQueryInProgress(true);
            LoyaltyApi.init()
                .buyPrize(user.uuid, prizeData.prize_uuid, quantity, user.ciam_token)
                .then((response) => {
                dispatch(fetchUserPoints({ useLocalCache: false, dispatchUpdateEvent: true }));
                setRewardCodes({ codes: response.codes, type: response.codes_type });
                setDialogStep(DIALOG_STEP.CONFIRMATION);
            })
                .catch((error) => {
                const preparedError = translateBackendErrorCode(error?.error_code, __(error.message));
                return toast.error(preparedError);
            })
                .finally(() => setIsQueryInProgress(false));
        }
        else {
            toast.error(__("mobile.error.session_timeout"));
        }
    };
    const handleRedeemPrize = () => {
        buyPrize();
        dispatchTrackingEvent(TrackingEvents.TrackingRedeemCheckout, { reward: prizeData.name });
    };
    return (_jsx(Dialog, { isOpened: !!prizeData?.prize_uuid, onClose: handleCloseDialog, closable: !isQueryInProgress, children: _jsxs("div", { className: styles.container, "data-step": dialogStep, children: [_jsx(Typography, { type: "h1", className: styles.title, color: "black", dataTestid: "redeemDialogSummaryTitle", children: dialogStep === DIALOG_STEP.SUMMARY ? labels.dialogTitle : labels.dialogTitleConfirmation }), _jsx(MessageBox, { variant: MessageBoxVariant.message, isVisible: dialogStep === DIALOG_STEP.CONFIRMATION, icon: CheckMark, className: styles.messageBox, children: _jsx("p", { dangerouslySetInnerHTML: { __html: sanitize(confirmationLabel) } }) }), _jsx("div", { className: styles.imageHolder, children: _jsx("img", { src: prizeData.images[1], alt: "", "data-testid": "redeemDialogImage" }) }), _jsx(DialogCard, { pointsBalance: pointsBalance, labels: {
                        costLabel,
                        pointsLabel,
                        availablePointsLabel,
                        balanceAfterRedemptionLabel,
                        emailLabel,
                        emailHint,
                        codeLabel,
                        codeInfo,
                        externalCodeButtonLabel,
                        unitLabel,
                    }, prizeData: prizeData, dialogStep: dialogStep, codes: codes, type: type, quantity: { val: quantity, set: setQuantity } }), dialogStep === DIALOG_STEP.SUMMARY && (_jsxs(_Fragment, { children: [_jsx(RedeemDialogButtons, { primaryCtaLabel: primaryCta, secondaryCtaLabel: secondaryCta, handleCloseDialog: handleCloseDialog, handleRedeemPrize: handleRedeemPrize, isQueryInProgress: isQueryInProgress }), _jsx("div", { className: styles.bottomText, children: _jsx(Info, { message: _jsx("p", { dangerouslySetInnerHTML: { __html: sanitize(labels.bottomText) } }) }) })] }))] }) }));
};
//# sourceMappingURL=RedeemDialog.js.map